// Per-employee data-readiness surface (Increment B3): a 0-100 completeness
// score with per-bucket breakdowns (profile / statutory / attendance / contact),
// an expandable missing-field list, and duplicate-value warnings.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Progress } from '@/features/shared/components/ui/progress';
import { GaugeCircle, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, FileText, ArrowRight, UserCheck } from 'lucide-react';
import type { EmployeeReadiness } from '@/lib/hrApi';

type Tone = 'green' | 'amber' | 'red';

const toneFor = (score: number, hasDuplicates: boolean): Tone => {
  if (hasDuplicates || score < 50) return 'red';
  if (score >= 90) return 'green';
  return 'amber';
};

const TONE_BADGE: Record<Tone, string> = {
  green: 'bg-success-soft text-success-strong border-transparent',
  amber: 'bg-warning-soft text-warning-strong border-transparent',
  red: 'bg-danger-soft text-danger-strong border-transparent',
};

/** Small header badge: green ≥90 "Complete", amber 50-89 "Incomplete",
 *  red <50 (or any duplicates) "Action needed". */
export const ReadinessBadge: React.FC<{ readiness: EmployeeReadiness }> = ({ readiness }) => {
  const { t } = useTranslation('employees');
  const tone = toneFor(readiness.score, (readiness.duplicates || []).length > 0);
  const label =
    tone === 'green'
      ? t('readiness.badge_complete', { defaultValue: 'Complete' })
      : tone === 'amber'
        ? t('readiness.badge_incomplete', { defaultValue: 'Incomplete' })
        : t('readiness.badge_action', { defaultValue: 'Action needed' });
  return (
    <Badge className={`border ${TONE_BADGE[tone]}`} title={t('readiness.badge_title', { defaultValue: 'Data readiness: {{score}}%', score: readiness.score })}>
      {tone === 'green' ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
      {label} · {readiness.score}%
    </Badge>
  );
};

const BUCKET_ORDER = ['profile', 'statutory', 'attendance', 'contact', 'documents'] as const;

export const ReadinessCard: React.FC<{
  readiness: EmployeeReadiness;
  /** In Draft the card becomes an activation checklist: expanded by default,
   *  with a Fix action per missing field that jumps to the right editor. */
  draft?: boolean;
  employeeName?: string;
  /** Jump to the field's editor (tab). code is the readiness field code. */
  onFix?: (code: string, bucket: string) => void;
}> = ({ readiness, draft = false, employeeName, onFix }) => {
  const { t } = useTranslation('employees');
  const [expanded, setExpanded] = useState(draft); // Draft: show the to-do up front

  const bucketLabel: Record<(typeof BUCKET_ORDER)[number], string> = {
    profile: t('readiness.bucket_profile', { defaultValue: 'Profile' }),
    statutory: t('readiness.bucket_statutory', { defaultValue: 'Statutory' }),
    attendance: t('readiness.bucket_attendance', { defaultValue: 'Attendance' }),
    contact: t('readiness.bucket_contact', { defaultValue: 'Contact' }),
    documents: t('readiness.bucket_documents', { defaultValue: 'Documents' }),
  };

  const missingAll = BUCKET_ORDER.flatMap((k) =>
    (readiness.buckets?.[k]?.missing || []).map((m) => ({ ...m, bucket: bucketLabel[k], bucketKey: k })),
  );
  const duplicates = readiness.duplicates || [];
  const tone = toneFor(readiness.score, duplicates.length > 0);
  // Recolor the Progress indicator (its single child div) by tone.
  const barColor =
    tone === 'green' ? '[&>div]:bg-success' : tone === 'amber' ? '[&>div]:bg-warning' : '[&>div]:bg-danger';

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader>
        <CardTitle className="text-base text-orange-700 flex items-center gap-2">
          {draft
            ? <><UserCheck className="w-4 h-4" /> {t('readiness.activate_title', { defaultValue: 'To activate this employee' })}</>
            : <><GaugeCircle className="w-4 h-4" /> {t('readiness.title', { defaultValue: 'Data readiness' })}</>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {draft && (
          <p className="text-sm text-muted-foreground">
            {missingAll.length > 0
              ? t('readiness.activate_intro', {
                  defaultValue: '{{name}} stays in Draft until these required fields are complete. Fix each one, then complete onboarding below.',
                  name: employeeName || t('readiness.this_employee', { defaultValue: 'This employee' }),
                })
              : t('readiness.activate_ready', {
                  defaultValue: 'All required fields are captured — {{name}} is ready to activate. Complete onboarding below to make them Active.',
                  name: employeeName || t('readiness.this_employee', { defaultValue: 'This employee' }),
                })}
          </p>
        )}
        {/* Overall score */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">
              {t('readiness.score_label', { defaultValue: 'Payroll & records completeness' })}
            </span>
            <span className="text-sm font-semibold text-foreground">{readiness.score}%</span>
          </div>
          <Progress value={readiness.score} className={`h-2 ${barColor}`} aria-label={t('readiness.title', { defaultValue: 'Data readiness' })} />
        </div>

        {/* Per-bucket chips */}
        <div className="flex flex-wrap gap-2">
          {BUCKET_ORDER.map((k) => {
            const b = readiness.buckets?.[k];
            if (!b) return null;
            const bt = toneFor(b.score, false);
            return (
              <Badge key={k} className={`border ${TONE_BADGE[bt]}`}>
                {k === 'documents' && <FileText className="w-3 h-3 mr-1" />}
                {bucketLabel[k]} {b.score}% ({b.passed}/{b.total})
              </Badge>
            );
          })}
        </div>

        {/* Duplicates */}
        {duplicates.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-danger" /> {t('readiness.duplicates', { defaultValue: 'Duplicate values' })}:
            </span>
            {duplicates.map((d) => (
              <Badge key={d} className={`border ${TONE_BADGE.red}`}>{d}</Badge>
            ))}
          </div>
        )}

        {/* Missing fields (expandable) */}
        {missingAll.length > 0 ? (
          <div>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 text-sm text-orange-700 hover:underline"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {t('readiness.missing_count', { defaultValue: '{{count}} missing field(s)', count: readiness.missing_count ?? missingAll.length })}
            </button>
            {expanded && (
              <ul className="mt-2 space-y-1">
                {missingAll.map((m) => (
                  <li key={`${m.bucket}-${m.code}`} className="text-sm text-foreground flex items-center gap-2 py-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-warning inline-block shrink-0" />
                    <span>{m.label}</span>
                    <span className="text-xs text-muted-foreground">· {m.bucket}</span>
                    {onFix && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 ml-auto text-xs text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-500/10"
                        onClick={() => onFix(m.code, m.bucketKey)}
                      >
                        {t('readiness.fix', { defaultValue: 'Fix' })}
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-success" /> {t('readiness.all_set', { defaultValue: 'All required fields are captured.' })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ReadinessCard;
